import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PitchDetector } from 'pitchy';

// Human singing range labels mapped to approximate Hz positions (80–500 Hz log scale)
const FREQ_LABELS = [
  { label: 'Bass', hz: 85 },
  { label: 'Baritone', hz: 130 },
  { label: 'Tenor', hz: 180 },
  { label: 'Alto', hz: 250 },
  { label: 'Soprano', hz: 380 },
];

// Map a frequency to a 0–100% position on a log scale between 80–500 Hz
const freqToPercent = (hz) => {
  const minLog = Math.log2(80);
  const maxLog = Math.log2(500);
  const val = Math.log2(Math.max(80, Math.min(500, hz)));
  return ((val - minLog) / (maxLog - minLog)) * 100;
};

/**
 * CalibrationModal — standalone voice calibration dialog.
 *
 * Props:
 *   open           {boolean}         — controlled visibility
 *   onClose        {function}        — called when user cancels (no calibration saved)
 *   onCalibrated   {function(freq)}  — called with the median Hz once the user confirms
 */
const CalibrationModal = ({ open, onClose, onCalibrated }) => {
  const [currentPitch, setCurrentPitch] = useState(0);
  const [samples, setSamples] = useState([]);
  const [error, setError] = useState(null);
  const [isListening, setIsListening] = useState(false);

  const audioContextRef = useRef(null);
  const animationFrameRef = useRef(null);
  const samplesRef = useRef([]);
  const pitchDetectorRef = useRef(null); // MPM detector instance
  const calibRecorderRef = useRef(null); // MediaRecorder to capture calibration audio
  const calibChunksRef  = useRef([]);   // Accumulated audio chunks

  // ─── Start / stop mic ───────────────────────────────────────────────────────
  const startListening = useCallback(async () => {
    setError(null);
    setSamples([]);
    samplesRef.current = [];
    setCurrentPitch(0);

    try {
      // Disable all speech-processing DSP so iOS/Android don't suppress
      // the singing signal before it reaches the analyser — critical for BT HFP.
      // Do NOT request a specific sampleRate: the browser ignores it for BT devices
      // and it can cause getUserMedia to fail on some iOS versions.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1,
        },
      });
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
      audioContextRef.current = audioContext;
      if (audioContext.state === 'suspended') await audioContext.resume();

      // Adapt fftSize to actual device sample rate.
      // BT HFP may deliver 8000 or 16000 Hz; a 2048-frame at 8000 Hz = 256 ms latency.
      const sr = audioContext.sampleRate;
      const fftSize = sr <= 8000 ? 512 : sr <= 16000 ? 1024 : 2048;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = fftSize;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      pitchDetectorRef.current = PitchDetector.forFloat32Array(fftSize);
      const buffer = new Float32Array(fftSize);

      const detect = () => {
        analyser.getFloatTimeDomainData(buffer);
        const [pitch, clarity] = pitchDetectorRef.current.findPitch(buffer, audioContext.sampleRate);

        if (clarity >= 0.6 && pitch >= 80 && pitch <= 500) {
          setCurrentPitch(pitch);
          samplesRef.current = [...samplesRef.current, pitch];
          setSamples([...samplesRef.current]);
        }

        animationFrameRef.current = requestAnimationFrame(detect);
      };

      detect();
      setIsListening(true);
      audioContextRef.current._stream = stream;

      // Record the calibration audio for diagnostic upload.
      // Captured silently — used to analyse what the user actually sang
      // when their calibration is suspected to be wrong.
      try {
        calibChunksRef.current = [];
        calibRecorderRef.current = new MediaRecorder(stream);
        calibRecorderRef.current.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) calibChunksRef.current.push(e.data);
        };
        calibRecorderRef.current.start();
      } catch (_) {} // non-critical — silent if unsupported
    } catch (err) {
      setError('Microphone access denied. Please allow microphone access and try again.');
    }
  }, []);

  const stopListening = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      const ctx = audioContextRef.current;
      audioContextRef.current = null; // null immediately so nothing else touches it
      // Stop mic tracks first — instant, releases hardware mic indicator on iOS
      try { ctx._stream?.getTracks().forEach((t) => t.stop()); } catch (_) {}
      // Close AudioContext without awaiting — Safari's close() is slow;
      // calling it fire-and-forget prevents blocking the caller.
      try { ctx.close().catch(() => {}); } catch (_) {}
    }
    // Stop calibration recorder if still running
    try {
      if (calibRecorderRef.current && calibRecorderRef.current.state !== 'inactive') {
        calibRecorderRef.current.stop();
      }
    } catch (_) {}
    setIsListening(false);
  }, []);

  // ─── Lifecycle ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      startListening();
    } else {
      stopListening();
      // Reset display state when closed
      setCurrentPitch(0);
      setSamples([]);
      samplesRef.current = [];
      setError(null);
    }
    return () => stopListening();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handlers ────────────────────────────────────────────────────────────────
  const handleDone = () => {
    const allSamples = samplesRef.current;
    if (allSamples.length === 0) return;

    // Interquartile-mean: discard bottom + top 25%, average the middle 50%.
    // Drops early warm-up wobbles and outliers from loud room resonances.
    const sorted = [...allSamples].sort((a, b) => a - b);
    const q1 = Math.floor(sorted.length * 0.25);
    const q3 = Math.ceil(sorted.length * 0.75);
    const middle = sorted.slice(q1, q3);
    const calibratedFreq = middle.reduce((s, v) => s + v, 0) / middle.length;

    // Stop the rAF loop immediately so no more state updates fire
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    // Release mic hardware immediately (stops the mic-in-use indicator on iOS)
    if (audioContextRef.current) {
      const ctx = audioContextRef.current;
      audioContextRef.current = null;
      try { ctx._stream?.getTracks().forEach((t) => t.stop()); } catch (_) {}
      // Close async — do NOT await; Safari takes 1-3 s and would block onCalibrated
      try { ctx.close().catch(() => {}); } catch (_) {}
    }
    setIsListening(false);

    // Save and notify immediately — UI responds without waiting for ctx.close()
    localStorage.setItem('userSaFrequency', calibratedFreq.toString());
    window.dispatchEvent(new Event('calibrationChanged'));
    import('../../utils/audioEngine').then(({ playCalibrationDone }) => playCalibrationDone());
    onCalibrated(calibratedFreq);

    // ── Background upload of calibration audio ──────────────────────────
    // Captures what the user actually sang during calibration so we can
    // diagnose wrong-octave or noisy calibrations from S3.
    // Completely fire-and-forget — never blocks or shows any UI.
    ;(async () => {
      try {
        const stored = localStorage.getItem('evcs_user');
        if (!stored) return;
        const { sub: userId, email: userEmail } = JSON.parse(stored);

        // Give MediaRecorder.stop() ~250ms to flush its final chunk
        const recorder = calibRecorderRef.current;
        if (recorder && recorder.state !== 'inactive') {
          await new Promise((resolve) => {
            recorder.onstop = resolve;
            try { recorder.stop(); } catch (_) { resolve(); }
          });
        } else {
          await new Promise((r) => setTimeout(r, 250));
        }

        const chunks = calibChunksRef.current.slice();
        calibRecorderRef.current = null;
        calibChunksRef.current = [];
        if (!chunks.length) return;

        const blob = new Blob(chunks, { type: 'audio/webm' });
        const { uploadSessionLog } = await import('../../services/api');
        await uploadSessionLog(userId, null, blob, userEmail);
      } catch (_) {} // silent — diagnostic upload is never critical
    })();
    // ──────────────────────────────────────────────────────────────────
  };

  const handleCancel = () => {
    stopListening();
    onClose();
  };

  const handleRetry = () => {
    startListening();
  };

  const enoughSamples = samples.length >= 60; // ~3 seconds at 60fps

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a1a',
          color: 'white',
          borderRadius: 3,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#58CC02' }}>
              🎤 Voice Calibration
            </Typography>
            <IconButton onClick={handleCancel} sx={{ color: '#aaa' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography variant="body1" sx={{ color: '#aaa', mb: 3 }}>
            🎤 Sing a comfortable <strong>"Aaah"</strong> and hold steady for ~3 seconds
          </Typography>

          {/* Pitch display area */}
          <Box
            sx={{
              backgroundColor: '#0d0d0d',
              borderRadius: 2,
              p: 4,
              mb: 3,
              minHeight: 180,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {error ? (
              <>
                <Typography sx={{ color: '#f44336', mb: 2, textAlign: 'center' }}>
                  {error}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleRetry}
                  sx={{ color: '#58CC02', borderColor: '#58CC02' }}
                >
                  Try Again
                </Button>
              </>
            ) : currentPitch > 0 ? (
              <>
                {/* Pitch reading — fixed-height container prevents layout shift when digit count changes */}
                <Box sx={{ height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                  <Typography
                    variant="h2"
                    sx={{
                      color: '#58CC02',
                      fontWeight: 'bold',
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1,
                      minWidth: 180,
                      textAlign: 'center',
                    }}
                  >
                    {currentPitch.toFixed(1)} Hz
                  </Typography>
                </Box>

                {/* ── Pitch range meter ───────────────────────────────── */}
                <Box sx={{ width: '100%', maxWidth: 320, mb: 2, mt: 1 }}>
                  {/* Voice type labels */}
                  <Box sx={{ position: 'relative', height: 18, mb: 0.5 }}>
                    {FREQ_LABELS.map(({ label, hz }) => (
                      <Typography
                        key={label}
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          left: `${freqToPercent(hz)}%`,
                          transform: 'translateX(-50%)',
                          color: '#555',
                          fontSize: '0.6rem',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label}
                      </Typography>
                    ))}
                  </Box>

                  {/* Track */}
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: 12,
                      borderRadius: 6,
                      background: 'linear-gradient(to right, #1a6635, #58CC02, #ffc107, #f44336)',
                      boxShadow: '0 0 8px rgba(88,204,2,0.3)',
                    }}
                  >
                    {/* Needle */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${freqToPercent(currentPitch)}%`,
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        border: '3px solid #58CC02',
                        boxShadow: '0 0 6px rgba(88,204,2,0.8)',
                        transition: 'left 0.08s ease',
                        zIndex: 2,
                      }}
                    />
                  </Box>

                  {/* Hz range labels */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#555', fontSize: '0.6rem' }}>80 Hz</Typography>
                    <Typography variant="caption" sx={{ color: '#555', fontSize: '0.6rem' }}>500 Hz</Typography>
                  </Box>
                </Box>

                {/* Sample progress toward 60 samples */}
                <Typography variant="body2" sx={{ color: '#aaa', mb: 1 }}>
                  {samples.length} samples collected
                </Typography>
                <Box sx={{ width: '100%', maxWidth: 300, backgroundColor: '#333', borderRadius: 4, height: 8 }}>
                  <Box
                    sx={{
                      width: `${Math.min(100, (samples.length / 60) * 100)}%`,
                      backgroundColor: enoughSamples ? '#58CC02' : '#ffc107',
                      borderRadius: 4,
                      height: '100%',
                      transition: 'width 0.1s ease',
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: enoughSamples ? '#58CC02' : '#ffc107', mt: 1 }}>
                  {enoughSamples ? '✓ Ready to save!' : `Hold steady — ${Math.max(0, 60 - samples.length)} samples to go`}
                </Typography>
                {/* Pulsing live indicator */}
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: '#4caf50',
                    mt: 2,
                    animation: 'pulse 1s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.4, transform: 'scale(1.4)' },
                    },
                  }}
                />
              </>
            ) : (
              <>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    border: '3px solid #444',
                    borderTopColor: '#58CC02',
                    animation: 'spin 1s linear infinite',
                    mb: 2,
                    '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
                  }}
                />
                <Typography variant="h6" sx={{ color: '#aaa', mb: 1 }}>
                  Listening…
                </Typography>
                <Typography variant="body2" sx={{ color: '#555' }}>
                  Start singing your comfortable Sa note — any pitch is fine
                </Typography>
              </>
            )}
          </Box>

          {/* Actions */}
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              variant="text"
              onClick={handleCancel}
              sx={{ color: '#888' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDone}
              disabled={!enoughSamples}
              sx={{
                backgroundColor: enoughSamples ? '#58CC02' : '#444',
                color: 'white',
                fontWeight: 'bold',
                px: 3,
                '&:hover': { backgroundColor: enoughSamples ? '#46a302' : '#444' },
                '&.Mui-disabled': { backgroundColor: '#444', color: '#777' },
              }}
            >
              {enoughSamples ? 'Save Calibration' : 'Keep Singing…'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CalibrationModal;
